import {
  Account_External_Truck_Identifier_Read,
  Account_Invite_Internal_Equipment_Create,
  DriverClassification,
  Equipment_Create,
  Equipment_Read,
  Equipment_Read_Default_Nested,
  Equipment_Read_Minimal,
  Equipment_Read_Nested,
  Equipment_Read_Typeahead,
  Equipment_Update,
  EquipmentState,
  EquipmentType_Create,
  EquipmentType_Read,
  EquipmentType_Read_Nested,
  EquipmentType_Read_Typeahead,
  EquipmentType_Update,
  GrossCapacity,
  OrderUnitOfMeasure,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';

import { AccountBasic } from '~hooks/useAccount/models';
import { CompanyBasic } from '~hooks/useCompany/models';
import { CompanyShare, CompanyShareBasic } from '~hooks/useCompanyShares/models';
import { BasicLabel } from '~hooks/useLabels/models';
import { BasicMaterial } from '~hooks/useMaterials/models';
import { SiteBasic } from '~hooks/useSites/models';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';

const eqStateOptions = [
  { id: 'active', name: t('common.active') },
  { id: 'inactive', name: t('common.inactive') },
];

type EquipmentTypeReference = {
  id: string;
};

export function maxGrossWeightToNumber(maxGrossWeight: Nullable<number | string>) {
  return maxGrossWeight ? Number(maxGrossWeight) : null;
}

function overweightPermitNumberOrNull(overweightPermitNumber: Nullable<string>) {
  return overweightPermitNumber || null;
}

type EquipmentLabelReference = {
  id: string;
};

type EquipmentHomeBaseReference = {
  id?: Nullable<string>;
  siteId?: Nullable<string>;
};

type EquipmentOwnerFormValues = {
  ownedByAccount?: Nullable<{ value: string }>;
  shareName?: Nullable<string>;
  shareTicketMatchId?: Nullable<string>;
};

type EquipmentDeparseCreateInput = EquipmentOwnerFormValues & {
  name: string;
  externalId: Nullable<string>;
  licenseNumber: Nullable<string>;
  isNotActive: boolean;
  type: EquipmentTypeReference;
  accountingId: Nullable<string>;
  zone: Nullable<string>;
  homeBase: Nullable<EquipmentHomeBaseReference>;
  labels: EquipmentLabelReference[];
  overweightPermitNumber: Nullable<string>;
  maxGrossWeight: Nullable<number | string>;
};

type EquipmentDeparseInternalInput = {
  name: string;
  type: EquipmentTypeReference;
  isNotActive?: boolean;
  homeBase?: Nullable<EquipmentHomeBaseReference>;
};

export class EquipmentMinimal {
  constructor(
    private _id: string,
    private _name: string,
    private _externalId: string | null,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get externalId() {
    return this._externalId;
  }

  public static parse(proto: Equipment_Read_Minimal) {
    return new EquipmentMinimal(proto.id, proto.name, proto.external_id ?? null);
  }
}

export class EquipmentTypeBasic {
  constructor(
    private _id: string,
    private _name: string,
    private _system: boolean,
    private _discarded_at: Dayjs | null = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get system() {
    return this._system;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: EquipmentType_Read_Nested) {
    return new EquipmentTypeBasic(
      proto.id,
      proto.name,
      proto.system,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class EquipmentTypeItem {
  public static parse(proto: EquipmentType_Read): EquipmentTypeItem {
    return new EquipmentTypeItem(
      proto?.name || '',
      proto?.id ?? '',
      proto?.system ?? false,
      proto?.external_id,
      proto?.source ?? '',
      proto?.gross_capacity ?? '',
      proto?.gross_capacity_unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.gross_capacity_unit_of_measure,
            id: proto.gross_capacity_unit_of_measure,
          })
        : null,
      dayjs(proto?.created_at),
      proto?.accounting_id ?? null,
      proto?.additional_gross_capacities?.map(GrossCapacityItem.parse) ?? [],
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.driver_classification ?? null,
      proto.materials?.map(BasicMaterial.parse) ?? [],
    );
  }

  public static deparse(data: EquipmentTypeItem): EquipmentType_Create {
    return {
      name: data.name,
      external_id: data.externalId,
      gross_capacity: data.grossCapacity ? Number(data.grossCapacity) : null,
      gross_capacity_unit_of_measure: data.grossCapacityUnitOfMeasure
        ?.id as OrderUnitOfMeasure,
      accounting_id: data.accountingId,
      additional_gross_capacities: data.additionalGrossCapacities?.map(
        GrossCapacityItem.deparse,
      ),
      material_ids: data.materials?.map(({ id }) => id),
    };
  }

  public static deparseUpdate(data: EquipmentTypeItem): EquipmentType_Update {
    return {
      name: data.name,
      external_id: data.externalId,
      gross_capacity: data.grossCapacity ? Number(data.grossCapacity) : null,
      gross_capacity_unit_of_measure: data.grossCapacityUnitOfMeasure
        ?.id as OrderUnitOfMeasure,
      accounting_id: data.accountingId,
      additional_gross_capacities: data.additionalGrossCapacities.map(
        GrossCapacityItem.deparse,
      ),
    };
  }

  public get system(): boolean {
    return this._system;
  }
  public get name(): string {
    return this._name;
  }
  public get id(): string {
    return this._id;
  }
  public get externalId(): Nullable<string> {
    return this._externalId;
  }
  public get source(): string {
    return this._source;
  }
  public get grossCapacity(): string {
    return this._gross_capacity;
  }
  public get grossCapacityUnitOfMeasure(): Nullable<ItemNameAndId> {
    return this._gross_capacity_unit_of_measure;
  }
  public set grossCapacityUnitOfMeasure(value: Nullable<ItemNameAndId>) {
    this._gross_capacity_unit_of_measure = ItemNameAndId.parse(value);
  }
  public get createdAt(): Dayjs {
    return this._createdAt;
  }
  public get accountingId(): Nullable<string> {
    return this._accounting_id;
  }
  public get additionalGrossCapacities(): GrossCapacityItem[] {
    return this._additional_gross_capacities;
  }
  public get discardedAt() {
    return this._discarded_at;
  }
  public get driverClassification() {
    return this._driver_classification;
  }
  public get materials() {
    return this._materials;
  }
  // Any unit counts as ready; the route optimizer needs both a capacity and its unit.
  public get isCapacityMissing() {
    const hasPositiveCapacity = Number(this._gross_capacity) > 0;

    return !hasPositiveCapacity || !this._gross_capacity_unit_of_measure;
  }
  constructor(
    private _name: string,
    private _id: string,
    private _system: boolean,
    private _externalId: Nullable<string>,
    private _source: string,
    private _gross_capacity: string,
    private _gross_capacity_unit_of_measure: Nullable<ItemNameAndId>,
    private _createdAt: Dayjs,
    private _accounting_id: Nullable<string>,
    private _additional_gross_capacities: GrossCapacityItem[],
    private _discarded_at: Dayjs | null = null,
    private _driver_classification: Nullable<DriverClassification> = null,
    private _materials: BasicMaterial[] = [],
  ) {}
}
class GrossCapacityItem {
  public static parse(proto: GrossCapacity) {
    return new GrossCapacityItem(
      proto.capacity,
      ItemNameAndId.parse({
        name: proto.unit_of_measure,
        id: proto.unit_of_measure,
      }),
    );
  }

  public static deparse(data: GrossCapacityItem): GrossCapacity {
    return {
      capacity: data.capacity,
      unit_of_measure: data.unitOfMeasure.id as OrderUnitOfMeasure,
    };
  }

  public get capacity() {
    return this._capacity;
  }

  public set capacity(value: string) {
    this._capacity = value;
  }

  public get unitOfMeasure(): ItemNameAndId {
    return this._unit_of_measure;
  }

  public set unitOfMeasure(value: Nullable<ItemNameAndId>) {
    this._unit_of_measure = ItemNameAndId.parse(value ?? '');
  }

  constructor(
    private _capacity: string,
    private _unit_of_measure: ItemNameAndId,
  ) {}
}

export class EquipmentTypeTypeahead {
  constructor(
    private _id: string,
    private _name: string,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public static parse(proto: EquipmentType_Read_Typeahead) {
    return new EquipmentTypeTypeahead(proto.id, proto.name);
  }
}

export class EquipmentTypeahead extends ItemNameAndId {
  public static parse(proto: Equipment_Read_Typeahead): EquipmentTypeahead {
    return new EquipmentTypeahead(
      proto.id,
      proto.name,
      proto.external_id ?? null,
      EquipmentTypeBasic.parse(proto.equipment_type),
      proto.company_share ? CompanyShareBasic.parse(proto.company_share) : null,
      proto.equipment_id,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public get externalId(): Nullable<string> {
    return this._external_id;
  }

  public get equipmentType(): EquipmentTypeBasic {
    return this._equipment_type;
  }

  public get companyShare(): Nullable<CompanyShareBasic> {
    return this._company_share;
  }

  public get equipmentId() {
    return this._equipment_id;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    _id: string,
    _name: string,
    private _external_id: Nullable<string>,
    private _equipment_type: EquipmentTypeBasic,
    private _company_share: Nullable<CompanyShareBasic>,
    private _equipment_id: string,
    private _discarded_at: Dayjs | null = null,
  ) {
    super(_name, _id);
    this._equipment_id = _equipment_id;
  }
}

export class BasicEquipment extends EquipmentTypeahead {
  public static parse(
    proto: Equipment_Read_Nested & { license_number?: Equipment_Read['license_number'] },
  ): BasicEquipment {
    return new BasicEquipment(
      proto.id,
      proto.name,
      proto.external_id ?? null,
      EquipmentTypeBasic.parse(proto.equipment_type),
      proto.company_share ? CompanyShareBasic.parse(proto.company_share) : null,
      proto.license_number ?? null,
      proto.accounting_id ?? null,
      proto.zone ?? null,
      proto.equipment_id,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  constructor(
    _id: string,
    _name: string,
    _external_id: Nullable<string>,
    _equipment_type: EquipmentTypeBasic,
    _company_share: Nullable<CompanyShareBasic>,
    private _license_number: Nullable<string>,
    private _accounting_id: Nullable<string>,
    private _zone: Nullable<string>,
    _equipment_id: string,
    _discarded_at: Dayjs | null = null,
  ) {
    super(
      _id,
      _name,
      _external_id,
      _equipment_type,
      _company_share,
      _equipment_id,
      _discarded_at,
    );
  }

  public get licenseNumber(): Nullable<string> {
    return this._license_number;
  }

  public get accountingId(): Nullable<string> {
    return this._accounting_id;
  }

  public get zone(): Nullable<string> {
    return this._zone;
  }
}

export class Equipment {
  public static parse(proto: Equipment_Read) {
    const stateOption = eqStateOptions.find((option) => option.id === proto.state);
    return new Equipment(
      proto.id,
      proto.name,
      EquipmentTypeItem.parse(proto.equipment_type as EquipmentType_Read),
      CompanyBasic.parse(proto.company),
      proto.external_id ?? null,
      proto.license_number ?? null,
      proto.state ? ItemNameAndId.parse(stateOption) : null,
      dayjs(proto.created_at),
      proto.company_share ? CompanyShareBasic.parse(proto.company_share) : null,
      proto.accounting_id ?? null,
      proto.zone ?? null,
      proto.equipment_id,
      proto.home_base ? SiteBasic.parse(proto.home_base) : null,
      proto.labels.map((label) => BasicLabel.parse(label)),
      proto.compliant ?? null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.internal_company ? CompanyBasic.parse(proto.internal_company) : null,
      proto.overweight_permit_number ?? null,
      proto.max_gross_weight ?? null,
    );
  }
  public static deparseUpdate(data: Equipment & EquipmentOwnerFormValues) {
    const equipmentUpdate: Equipment_Update = {
      name: data.name,
      external_id: data.externalId,
      license_number: data.licenseNumber,
      state: data.isNotActive ? EquipmentState.INACTIVE : EquipmentState.ACTIVE,
      equipment_type_id: data.type?.id,
      accounting_id: data.accountingId,
      zone: data.zone,
      home_base_id: data.homeBase?.id ?? null,
      label_ids: data.labels?.map((label: BasicLabel) => label.id) ?? [],
      overweight_permit_number: overweightPermitNumberOrNull(data.overweightPermitNumber),
      max_gross_weight: maxGrossWeightToNumber(data.maxGrossWeight),
      ...Equipment.deparseOwner(data),
    };

    return equipmentUpdate;
  }
  public static deparseCreate(data: EquipmentDeparseCreateInput) {
    const payload: Equipment_Create = {
      name: data.name,
      external_id: data.externalId,
      license_number: data.licenseNumber,
      state: data.isNotActive ? EquipmentState.INACTIVE : EquipmentState.ACTIVE,
      equipment_type_id: data.type.id,
      accounting_id: data.accountingId,
      zone: data.zone,
      home_base_id: data.homeBase?.siteId ?? data.homeBase?.id ?? null,
      label_ids: data.labels.map((label) => label.id),
      overweight_permit_number: overweightPermitNumberOrNull(data.overweightPermitNumber),
      max_gross_weight: maxGrossWeightToNumber(data.maxGrossWeight),
      ...Equipment.deparseOwner(data),
    };

    return payload;
  }

  private static deparseOwner(data: EquipmentOwnerFormValues) {
    if (!data.ownedByAccount?.value) {
      return {};
    }

    return {
      provision_for_company_id: data.ownedByAccount.value,
      share_name: data.shareName || null,
      share_ticket_match_id: data.shareTicketMatchId || null,
    };
  }

  public static deparseInternal(data: EquipmentDeparseInternalInput) {
    const payload: Account_Invite_Internal_Equipment_Create = {
      name: data.name,
      equipment_type_id: data.type.id,
      state: data.isNotActive ? EquipmentState.INACTIVE : EquipmentState.ACTIVE,
      home_base_id: data.homeBase?.siteId ?? data.homeBase?.id ?? null,
    };

    return payload;
  }

  public get name() {
    return this._name;
  }

  public get id() {
    return this._id;
  }

  public get type() {
    return this._type;
  }

  public get typeName() {
    return this._type.name;
  }

  public get company() {
    return this._company;
  }

  public get companyName() {
    return this._company.legalName;
  }

  public get isActive() {
    return this._state?.id === EquipmentState.ACTIVE;
  }
  public set isActive(value: boolean) {
    this._state = value
      ? ItemNameAndId.parse(eqStateOptions?.[0])
      : ItemNameAndId.parse(eqStateOptions?.[1]);
  }

  public get isNotActive() {
    return !this.isActive;
  }
  public set isNotActive(value: boolean) {
    this.isActive = !value;
  }

  public get externalId() {
    return this._externalId;
  }

  public get licenseNumber() {
    return this._licenseNumber;
  }

  public get internalCompany() {
    return this._internal_company;
  }

  public get overweightPermitNumber() {
    return this._overweight_permit_number;
  }

  public get maxGrossWeight() {
    return this._max_gross_weight;
  }

  public get state() {
    return this._state;
  }

  public get createdAt() {
    return this._createdAt;
  }

  public get companyShare() {
    return this._company_share;
  }

  public get accountingId() {
    return this._accounting_id;
  }

  public get zone() {
    return this._zone;
  }

  public get equipmentId() {
    return this._equipment_id;
  }

  public get homeBase() {
    return this._homeBase;
  }

  public get labels() {
    return this._labels;
  }

  public get compliant() {
    return this._compliant;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    private _id: string,
    private _name: string,
    private _type: EquipmentTypeItem, // Temporary ? til we have full model ?
    private _company: CompanyBasic,
    private _externalId: Nullable<string>,
    private _licenseNumber: Nullable<string>,
    private _state: Nullable<ItemNameAndId>,
    private _createdAt: Dayjs,
    private _company_share: Nullable<CompanyShareBasic>,
    private _accounting_id: Nullable<string>,
    private _zone: Nullable<string>,
    private _equipment_id: string,
    private _homeBase: Nullable<SiteBasic>,
    private _labels: BasicLabel[],
    private _compliant: boolean | null = null,
    private _discarded_at: Dayjs | null = null,
    private _internal_company: Nullable<CompanyBasic> = null,
    private _overweight_permit_number: Nullable<string> = null,
    private _max_gross_weight: Nullable<string> = null,
  ) {}
}

export class BasicEquipmentDefault {
  constructor(
    private _id: string,
    private _name: string,
    private _external_id: Nullable<string>,
    private _equipment_type: EquipmentTypeBasic,
    private _company_share: Nullable<CompanyShareBasic>,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get externalId() {
    return this._external_id;
  }

  public get equipmentType() {
    return this._equipment_type;
  }

  public get companyShare() {
    return this._company_share;
  }

  public static parse(proto: Equipment_Read_Default_Nested) {
    return new BasicEquipmentDefault(
      proto.id,
      proto.name,
      proto.external_id,
      EquipmentTypeBasic.parse(proto.equipment_type),
      proto.company_share ? CompanyShareBasic.parse(proto.company_share) : null,
    );
  }
}

export class AccountExternalTruckIdentifier {
  public static parse(
    proto: Account_External_Truck_Identifier_Read,
  ): AccountExternalTruckIdentifier {
    return new AccountExternalTruckIdentifier(
      proto.id,
      proto.external_truck_id,
      proto.last_ticket_received_at,
      AccountBasic.parse(proto.account),
      AccountBasic.parse(proto.vendor_account),
      proto.customer_account ? AccountBasic.parse(proto.customer_account) : null,
      proto.company_share ? CompanyShare.parse(proto.company_share) : null,
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
    );
  }

  public get id() {
    return this._id;
  }

  public get externalTruckId() {
    return this._external_truck_id;
  }

  public get lastTicketReceivedAt() {
    return this._last_ticket_received_at;
  }

  public get account() {
    return this._account;
  }

  public get vendorAccount() {
    return this._vendor_account;
  }

  public get customerAccount() {
    return this._customer_account;
  }

  public get companyShare() {
    return this._company_share;
  }

  public set companyShare(value: Nullable<CompanyShare>) {
    this._company_share = value;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  constructor(
    private _id: string,
    private _external_truck_id: string,
    private _last_ticket_received_at: Nullable<string>,
    private _account: AccountBasic,
    private _vendor_account: AccountBasic,
    private _customer_account: Nullable<AccountBasic>,
    private _company_share: Nullable<CompanyShare>,
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
  ) {}
}
